import { useCallback, useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { DeskWatchedValues } from 'components/events/EventForm/types'
import { deskSourceSides, START_TIME_PLACEHOLDER, timeNotStated } from 'pages/Desk/deskPrefill'
import type { DeskTownChange } from 'pages/Desk/useDeskTownChange'
import type { ApplicationRow } from 'supabase/desk/deskTypes'
import {
  buildRecheckBody,
  isRecheckEligible,
  recheckKeyOf,
  recheckMatch
} from 'supabase/desk/recheckMatch'
import type { DeskRecheckOutcome, DeskRecheckReason } from 'supabase/desk/recheckMatch'
import { isDeskTranslationQuery } from './useDeskTranslation'

/**
 * The desk's duplicate re-check for one candidate (Phase 33.1).
 *
 * Driven by 33.3's useDeskTownChange (D-28): pass its result as `watch`. The
 * watcher already debounces the form's reports (its 500 ms) and
 * bumps `townVersion` / `startVersion` only when the applied town or start
 * moved, so this hook has no timer and no debounce of its own: it fires when
 * a version goes up and the city | UTC start day key differs from the last
 * one it asked about (which starts at the baseline's). A time-only change or
 * a typed town that keeps city_id null asks nothing.
 *
 * Single flight (D-10): at most one request per candidate is in flight, so a
 * superseded request's RPC write can never commit after a newer one. When it
 * settles and a newer key arrived meanwhile, the newer one is sent at once
 * and the settled answer is not shown (a written answer still refreshes the
 * candidate, so the screen matches the row).
 *
 * `pending` (D-30) is true from the render in which the watcher applied a
 * change that needs a call until the answer for the latest key has settled
 * and its candidate refetch is done, so the Match panel can hold Attach for
 * the whole window. The watcher's own 500 ms before it applies is accepted.
 *
 * Never a useQuery: a ['desk', ...] query would re-run, and write again, on
 * every decision invalidation and focus refetch (D-25). It is an imperative
 * call. It never blocks or delays Approve (D-08): nothing here is awaited by
 * a decision.
 *
 * Not exported from the hooks/desk barrel: Desk.tsx imports that barrel, and
 * this hook pulls deskPrefill -> franc-min (ESM only), which CRA's Jest cannot
 * load in Desk.test.tsx. Import 'hooks/desk/useDeskRecheck' directly.
 */

export type DeskRecheckWatch = Pick<DeskTownChange, 'baseline' | 'watched' | 'townVersion' | 'startVersion'>

export type DeskRecheckFailure = {
  reason: DeskRecheckReason | 'network' | 'refused'
  retryable: boolean
}

export interface DeskRecheckOptions {
  /** The candidate was decided elsewhere (D-11): show the existing notice. */
  onAlreadyDecided?: () => void
}

export interface DeskRecheck {
  pending: boolean
  failure: DeskRecheckFailure | null
  retry: () => void
}

/** Only a failed shortlist read can come out differently on a second try. */
const RETRYABLE_REASONS: ReadonlyArray<DeskRecheckReason> = ['shortlist_unavailable']

const NETWORK_FAILURE: DeskRecheckFailure = { reason: 'network', retryable: true }

/** What the panel shows for a settled answer (null = nothing to report). */
const failureOf = (outcome: DeskRecheckOutcome): DeskRecheckFailure | null => {
  if (outcome.ok) {
    const { label, reason } = outcome.value
    if (label !== 'unchecked' || reason === null) return null
    return { reason, retryable: RETRYABLE_REASONS.includes(reason) }
  }
  switch (outcome.code) {
    case 'network':
    case 'misconfigured':
      return NETWORK_FAILURE
    case 'not_eligible':
    case 'already_decided':
      return null
    default:
      return { reason: 'refused', retryable: false }
  }
}

/** The row changed (or the screen is stale): refresh it. */
const changesRow = (outcome: DeskRecheckOutcome): boolean =>
  outcome.ok
    ? outcome.value.written
    : outcome.code === 'not_eligible' || outcome.code === 'already_decided'

interface RecheckView {
  id: number | undefined
  pending: boolean
  failure: DeskRecheckFailure | null
}

export const useDeskRecheck = (
  candidate: ApplicationRow | undefined,
  watch: DeskRecheckWatch,
  options: DeskRecheckOptions = {}
): DeskRecheck => {
  const queryClient = useQueryClient()

  const [view, setView] = useState<RecheckView>({ id: candidate?.id, pending: false, failure: null })

  // Latest values, read by the effect and by retry without depending on
  // object identity (a refetch hands a new candidate object every time).
  const candidateRef = useRef(candidate)
  candidateRef.current = candidate
  const watchRef = useRef(watch)
  watchRef.current = watch
  const optionsRef = useRef(options)
  optionsRef.current = options

  // The candidate the state belongs to, and a generation bumped on every
  // switch: an answer from an earlier generation is dropped on arrival.
  const idRef = useRef<number | undefined>(candidate?.id)
  const generationRef = useRef(0)
  // The last key asked about (null = the baseline's).
  const lastKeyRef = useRef<string | null>(null)
  // The latest watched values that still need a call.
  const wantedRef = useRef<DeskWatchedValues | null>(null)
  // The key of the request in flight (null = idle).
  const inFlightKeyRef = useRef<string | null>(null)

  const invalidate = useCallback(
    async (id: number) => {
      const isCandidate = (key: readonly unknown[]) =>
        key[0] === 'desk' && key[1] === 'candidate' && key[2] === id
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['desk', 'candidate', id] }),
        // The rest of the desk (queue, counts, raw item, siblings), as every
        // desk decision does; never the translation cache (DeepL quota), and
        // not the candidate twice.
        queryClient.invalidateQueries({
          queryKey: ['desk'],
          predicate: (query) => !isDeskTranslationQuery(query) && !isCandidate(query.queryKey)
        }),
        queryClient.invalidateQueries({ queryKey: ['application'] }),
        queryClient.invalidateQueries({ queryKey: ['applications'] })
      ])
    },
    [queryClient]
  )

  const pump = useCallback(async (): Promise<void> => {
    // A newer key is waiting: true when it differs from the key just sent.
    // The same key (A -> B -> A) needs no new request: it is dropped.
    const takesFollowUp = (sentKey: string): boolean => {
      const wanted = wantedRef.current
      if (wanted === null) return false
      if (recheckKeyOf(wanted) !== sentKey) return true
      wantedRef.current = null
      return false
    }

    if (inFlightKeyRef.current !== null) return
    const values = wantedRef.current
    const row = candidateRef.current
    if (values === null || !row) return
    wantedRef.current = null
    const generation = generationRef.current
    const key = recheckKeyOf(values)
    inFlightKeyRef.current = key
    try {
      const outcome = await recheckMatch(
        buildRecheckBody(
          row.id,
          values,
          deskSourceSides(row),
          timeNotStated(row).start ? START_TIME_PLACEHOLDER : null
        )
      )
      if (generation !== generationRef.current) {
        // Another candidate is open: nothing shown, no follow-up. A write
        // still changed the row, so its cache is refreshed quietly.
        if (changesRow(outcome)) void invalidate(row.id)
        return
      }

      if (takesFollowUp(key)) {
        if (changesRow(outcome)) void invalidate(row.id)
        inFlightKeyRef.current = null
        void pump()
        return
      }

      // The slot stays held through the refetch, so a change made meanwhile
      // waits for it and pending covers the whole window (D-30).
      if (changesRow(outcome)) await invalidate(row.id)
      if (generation !== generationRef.current) return
      inFlightKeyRef.current = null
      if (takesFollowUp(key)) {
        void pump()
        return
      }

      if (!outcome.ok && outcome.code === 'already_decided') {
        optionsRef.current.onAlreadyDecided?.()
      }
      setView({ id: row.id, pending: false, failure: failureOf(outcome) })
    } catch {
      // Never into React. An earlier candidate's throw must never free the
      // current candidate's slot (two requests at once).
      if (generation !== generationRef.current) return
      inFlightKeyRef.current = null
      wantedRef.current = null
      setView({ id: row.id, pending: false, failure: NETWORK_FAILURE })
    }
  }, [invalidate])

  const candidateId = candidate?.id

  useEffect(() => {
    if (idRef.current !== candidateId) {
      generationRef.current += 1
      idRef.current = candidateId
      wantedRef.current = null
      inFlightKeyRef.current = null
      lastKeyRef.current = null
      setView({ id: candidateId, pending: false, failure: null })
    }

    const current = watchRef.current
    if (current.watched === null) return
    if (current.townVersion === 0 && current.startVersion === 0) return
    const key = recheckKeyOf(current.watched)
    const last = lastKeyRef.current ?? (current.baseline ? recheckKeyOf(current.baseline) : null)
    if (key === last) return
    const row = candidateRef.current
    if (!row || !isRecheckEligible(row)) return

    lastKeyRef.current = key
    wantedRef.current = current.watched
    setView({ id: row.id, pending: true, failure: null })
    void pump()
  }, [candidateId, watch.townVersion, watch.startVersion, pump])

  const retry = useCallback(() => {
    const row = candidateRef.current
    if (!row || row.id !== idRef.current || !isRecheckEligible(row)) return
    if (inFlightKeyRef.current !== null) return
    const current = watchRef.current
    const values = current.watched ?? current.baseline
    if (values === null) return
    lastKeyRef.current = recheckKeyOf(values)
    wantedRef.current = values
    setView({ id: row.id, pending: true, failure: null })
    void pump()
  }, [pump])

  // Until the switch effect runs, never show the previous candidate's state.
  const own = view.id === candidateId
  return {
    pending: own ? view.pending : false,
    failure: own ? view.failure : null,
    retry
  }
}
